import {
  getPerformanceSummaryRowMetricIds,
  getRiskSummaryRowMetricIds,
} from "./summary-row-metric-ids"

export interface TemplateMetricIdsOptions {
  prefix: string
  selectedCategory?: string
  selectedMetricDisplay?: string
  includeAllSubCategories?: boolean
}

function mergeSummaryAndCategoryMetricIds(summaryMetricIds: string[], categoryMetricIds: string[]) {
  return [...summaryMetricIds, ...categoryMetricIds.filter((metricId) => !summaryMetricIds.includes(metricId))]
}

export function getTemplateMetricIds(
  flattenedMetrics: Record<string, unknown> | null | undefined,
  { prefix, selectedCategory, selectedMetricDisplay, includeAllSubCategories = false }: TemplateMetricIdsOptions,
) {
  if (!flattenedMetrics || !prefix) {
    return []
  }

  return Object.keys(flattenedMetrics).filter((metricId) => {
    if (!metricId.startsWith(`${prefix}.`)) {
      return false
    }

    if (includeAllSubCategories) {
      return true
    }

    const parts = metricId.split(".")
    const subCategory = parts[1]
    const displayType = parts[parts.length - 1]

    if (selectedMetricDisplay && displayType !== selectedMetricDisplay) {
      return false
    }

    if (!selectedCategory) {
      return true
    }

    if (selectedCategory === "factors") {
      return subCategory === "factorAggregates"
    }

    return subCategory === selectedCategory
  })
}

function getPerformanceViewDuplicateMetricIds(
  flattenedMetrics: Record<string, unknown>,
  { selectedCategory, selectedMetricDisplay }: TemplateMetricIdsOptions,
) {
  const summaryMetricIds = getPerformanceSummaryRowMetricIds(
    flattenedMetrics,
    selectedMetricDisplay ?? "",
  )

  const categoryMetricIds = getTemplateMetricIds(flattenedMetrics, {
    prefix: "performance",
    selectedCategory,
    selectedMetricDisplay,
  })

  return mergeSummaryAndCategoryMetricIds(summaryMetricIds, categoryMetricIds)
}

function getRiskViewDuplicateMetricIds(
  flattenedMetrics: Record<string, unknown>,
  { selectedCategory }: TemplateMetricIdsOptions,
) {
  const summaryMetricIds = getRiskSummaryRowMetricIds(flattenedMetrics)

  const categoryMetricIds = getTemplateMetricIds(flattenedMetrics, {
    prefix: "risk",
    selectedCategory,
  })

  return mergeSummaryAndCategoryMetricIds(summaryMetricIds, categoryMetricIds)
}

export function getViewDuplicateMetricIds(
  flattenedMetrics: Record<string, unknown> | null | undefined,
  metricOptions: TemplateMetricIdsOptions,
) {
  if (!flattenedMetrics) {
    return []
  }

  const { prefix } = metricOptions

  if (prefix === "performance") {
    return getPerformanceViewDuplicateMetricIds(flattenedMetrics, metricOptions)
  }

  if (prefix === "risk") {
    return getRiskViewDuplicateMetricIds(flattenedMetrics, metricOptions)
  }

  return getTemplateMetricIds(flattenedMetrics, metricOptions)
}
